import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, textStyle } from "@passfirst/ui";

import { ChevronRightIcon, getCredentialIcon } from "../icons";
import { type Colors, useColors } from "../theme";

interface Props {
  slug: string;
  /** Bold top line — the full credential name per spec. */
  name: string;
  /** Muted bottom line — scope hint (e.g. "CDL · all states"). */
  meta: string;
  selected: boolean;
  onPress: () => void;
}

// Single row inside the welcome credential picker. Selected state
// adopts the 2px navy border + brand-soft fill convention shared with
// other picker chips (state grid, theme toggle).
export function CredentialRow({
  slug,
  name,
  meta,
  selected,
  onPress,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const Icon = getCredentialIcon(slug);
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      accessibilityLabel={`${name}, ${meta}`}
      style={[styles.row, selected && styles.rowSelected]}
    >
      <View style={styles.iconTile}>
        <Icon size={22} color={colors.accentSoftText} />
      </View>
      <View style={styles.labels}>
        <Text style={styles.name}>{name}</Text>
        <Text style={styles.meta}>{meta}</Text>
      </View>
      <ChevronRightIcon size={18} color={colors.textMuted} />
    </Pressable>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      padding: 15,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 14,
      backgroundColor: colors.surface,
    },
    rowSelected: {
      borderColor: colors.borderPicked,
      borderWidth: 2,
      padding: 14,
      backgroundColor: colors.surfacePicked,
    },
    iconTile: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfacePicked,
      alignItems: "center",
      justifyContent: "center",
    },
    labels: { flex: 1, gap: 2 },
    name: {
      fontFamily: fontFamily.bold,
      fontSize: 16,
      color: colors.textPrimary,
    },
    meta: {
      ...textStyle.caption,
      color: colors.textMuted,
    },
  });
}
